// Maps: the editor on the active map, its map menu (pick, new, save as, rename, duplicate, delete), save, and add a map's projection surfaces to the project as stage shapes.
import { objectPinKeys } from '~/lib/livePins'
import { mapPath, newMapData, outputToStage, resizeOutput } from '~/lib/map'
import { releaseMap } from '~/lib/map/held'
import { mapThumbnail } from '~/lib/map/thumb'
import { positionFromOrigin } from '~/lib/objectBounds'
import { resetPathOrigin } from '~/lib/svg/paths'
import api from '~/store/api'
import { selectIsExamples, selectIsRunner, selectIsTemplate, selectProject } from '~/store/app/selectors'
import { addObjects, groupObjects, sendObjectToBack, updateObject, updateObjects } from '~/store/live'
import { closeMapEditor, editMap, markMapSaved, openMapEditor, setActiveMapId, setMapEditor } from '~/store/maps'
import { selectActiveMapId, selectMapDirty, selectMaps } from '~/store/maps/selectors'
import { registerAction, resolveAction } from '~/store/middleware/confirmActions'
import { endBatch, startBatch } from '~/store/middleware/history'
import { setProjectionMap } from '~/store/outputMapping'
import { pinKeys } from '~/store/playback/pinThunks'
import { pushError, requestConfirm, requestPrompt } from '~/store/ui'

const NO_FILL = '#00000000'
const CUT_FILL = '#000000'

const mapsNow = async dispatch => {
	const { data } = await dispatch(api.endpoints.getMaps.initiate(undefined, { forceRefetch: true }))
	return data ?? []
}

/** The editor on map `id` as saved, on `tab`. */
const editNow = (id, tab) => (dispatch, getState) => {
	const map = selectMaps(getState()).find(m => m.id === id)
	if (!map) return false
	releaseMap()
	dispatch(openMapEditor({ id, name: map.name, data: map.data, tab }))
	dispatch(setProjectionMap(true))
	return true
}

/** Map `id` becomes active: this window shows it, the open editor edits it, and a writable project remembers it. */
const activateNow = id => (dispatch, getState) => {
	dispatch(setActiveMapId(id))
	const e = getState().maps.editing
	if (e && e.id !== id) dispatch(editNow(id, e.tab))
	const s = getState()
	const project = selectProject(s)
	if (!project?.id || !selectIsRunner(s) || selectIsTemplate(s) || selectIsExamples(s)) return
	const settings = project.settings ?? {}
	if (settings.map_id === id) return
	// ⚠️ The WHOLE settings: the server replaces the field, so a partial one would drop the thumbnail.
	dispatch(api.endpoints.updateProject.initiate({ id: project.id, settings: { ...settings, map_id: id } }))
}

/** The editor on the active map. */
export const openActiveMap = () => (dispatch, getState) => {
	const id = selectActiveMapId(getState())
	if (id) dispatch(editNow(id))
}

const closeNow = () => dispatch => {
	releaseMap()
	dispatch(closeMapEditor())
	dispatch(setProjectionMap(false))
}
registerAction('closeMap', () => closeNow())

/** Saves the draft or drops its edits, then runs `next`. */
const leaveNow =
	({ next, save }) =>
	async (dispatch, getState) => {
		if (save && !(await dispatch(saveMap()))) return
		const e = getState().maps.editing
		if (!save && e) dispatch(editNow(e.id, e.tab))
		dispatch(resolveAction(next))
	}
registerAction('leaveMap', params => leaveNow(params))

/** Runs `next` (an action descriptor), asking first whether an unsaved draft is saved or discarded. */
const leaveDraft = next => (dispatch, getState) => {
	const s = getState()
	if (!selectMapDirty(s)) return dispatch(resolveAction(next))
	dispatch(
		requestConfirm({
			title: 'Unsaved Map',
			message: `Save your changes to "${s.maps.editing.name}"?`,
			action: { type: 'leaveMap', params: { next, save: true } },
			noAction: { type: 'leaveMap', params: { next } },
			yesLabel: 'Save',
			noLabel: 'Discard',
		})
	)
}

/** Closes the editor, asking first when the draft is unsaved. */
export const closeMap = () => leaveDraft({ type: 'closeMap' })

/** Drops the draft's edits and closes the editor. */
export const cancelMap = () => leaveNow({ next: { type: 'closeMap' } })

/** Saves the draft and closes the editor; a failed save keeps it open. */
export const saveAndCloseMap = () => (dispatch, getState) =>
	dispatch(leaveNow({ next: { type: 'closeMap' }, save: selectMapDirty(getState()) }))

registerAction('activateMap', id => activateMap(id))

/** Map `id` becomes active and the editor edits it; unsaved edits ask first. */
export const pickMap = id => leaveDraft({ type: 'activateMap', params: id })

/** Asks for a name, then a new map; `copy` takes the draft (Save as), else it starts empty (New). */
export const promptNewMap = copy => (dispatch, getState) =>
	dispatch(
		requestPrompt({
			title: copy ? 'Save Map As' : 'New Map',
			inputValue: copy ? `${getState().maps.editing?.name ?? ''} copy` : '',
			inputPlaceholder: 'Map name',
			action: { type: copy ? 'saveMapAs' : 'newMap' },
		})
	)
// The dialog refuses an empty name; these guard the action run directly.
registerAction('newMap', ({ inputValue }) => {
	const name = inputValue.trim()
	return name ? leaveDraft({ type: 'createMap', params: { name } }) : () => {}
})
// The draft goes to the new map; the map it came from keeps its saved version.
registerAction('saveMapAs', ({ inputValue }) => (dispatch, getState) => {
	const name = inputValue.trim()
	if (name) dispatch(createMap(name, getState().maps.editing?.draft))
})
registerAction('createMap', ({ name }) => createMap(name))

/** The edited map, saved, copied as a new map that becomes active; unsaved edits ask first. */
export const duplicateMap = () => (dispatch, getState) => {
	const id = getState().maps.editing?.id
	if (id) dispatch(leaveDraft({ type: 'duplicateMap', params: id }))
}
registerAction('duplicateMap', id => (dispatch, getState) => {
	const map = selectMaps(getState()).find(m => m.id === id)
	if (map) dispatch(createMap(`${map.name} copy`, map.data))
})

export const promptRenameMap = () => (dispatch, getState) => {
	const e = getState().maps.editing
	if (!e) return
	dispatch(
		requestPrompt({
			title: 'Rename Map',
			inputValue: e.name,
			inputPlaceholder: 'Map name',
			action: { type: 'renameMap', params: { id: e.id } },
		})
	)
}
// The editor takes the name at once, so a save in flight carries it; a refused rename gives the old one back.
registerAction('renameMap', ({ id, inputValue }) => async (dispatch, getState) => {
	const name = inputValue.trim()
	if (!name) return
	const was = getState().maps.editing?.id === id ? getState().maps.editing.name : null
	if (was != null) dispatch(setMapEditor({ name }))
	const res = await dispatch(api.endpoints.updateMap.initiate({ id, name }))
	const e = getState().maps.editing
	if (res.error && was != null && e?.id === id && e.name === name) dispatch(setMapEditor({ name: was }))
})

export const deleteEditedMap = () => (dispatch, getState) => {
	const e = getState().maps.editing
	if (!e) return
	dispatch(
		requestConfirm({
			title: 'Delete Map',
			message: `Delete "${e.name}"? Projects keep the shapes they loaded from it.`,
			action: { type: 'deleteEditedMap', params: e.id },
			yesLabel: 'Delete',
			destructive: true,
		})
	)
}
// The editor moves to the map the outputs fall back to; with none left, it closes and `ensureDefaultMap` makes one.
registerAction('deleteEditedMap', id => async (dispatch, getState) => {
	const res = await dispatch(api.endpoints.deleteMap.initiate(id))
	if (res.error) return
	await mapsNow(dispatch)
	const next = selectActiveMapId(getState())
	if (!next) return dispatch(closeNow())
	dispatch(activateNow(next))
})

const resizeNow =
	({ width, height, keep }) =>
	(dispatch, getState) => {
		const draft = getState().maps.editing?.draft
		if (!draft) return
		releaseMap()
		dispatch(editMap(resizeOutput(draft, width, height, keep)))
	}
registerAction('resizeMapOutput', params => resizeNow(params))

/** The output at `width`×`height`; a new aspect asks whether shapes keep their proportions or stretch. */
export const resizeMapOutput = (width, height) => (dispatch, getState) => {
	const { output } = getState().maps.editing?.draft ?? {}
	if (!output || (width === output.width && height === output.height)) return
	if (Math.abs(width / height - output.width / output.height) < 1e-3)
		return dispatch(resizeNow({ width, height, keep: false }))
	dispatch(
		requestConfirm({
			title: 'New Aspect Ratio',
			message: `${output.width}×${output.height} becomes ${width}×${height}. Keep the surfaces' proportions, or stretch them to the new shape?`,
			action: { type: 'resizeMapOutput', params: { width, height, keep: true } },
			noAction: { type: 'resizeMapOutput', params: { width, height, keep: false } },
			yesLabel: 'Keep proportions',
			noLabel: 'Stretch',
		})
	)
}

export const saveMap = () => async (dispatch, getState) => {
	const e = getState().maps.editing
	if (!e) return
	const { draft, saved } = e
	dispatch(markMapSaved())
	// The cache takes the draft at once, so closing before the refetch never shows the old map.
	const patch = dispatch(
		api.util.updateQueryData('getMaps', undefined, list => {
			const m = list.find(x => x.id === e.id)
			if (m) Object.assign(m, { name: e.name, data: draft })
		})
	)
	const thumbnail = await mapThumbnail(draft)
	const res = await dispatch(api.endpoints.updateMap.initiate({ id: e.id, name: e.name, data: draft, thumbnail }))
	if (res.error) {
		patch.undo()
		if (getState().maps.editing?.id === e.id) dispatch(markMapSaved(saved))
		dispatch(pushError({ code: 'SAVE_MAP', title: 'Save Map', message: 'The map could not be saved.' }))
		return false
	}
	return true
}

/** Creates map `name` from `data` (else a new one) and makes it active. */
const createMap =
	(name, data = newMapData()) =>
	async dispatch => {
		const res = await dispatch(api.endpoints.addMap.initiate({ name, data, thumbnail: await mapThumbnail(data) }))
		if (!res.data?.id) return res
		await mapsNow(dispatch)
		dispatch(activateMap(res.data.id))
		return res
	}

let ensuring = null
/** A user always has a map: with none, a 1:1 `Default` is made. */
export const ensureDefaultMap = () => dispatch => {
	ensuring ??= (async () => {
		const data = newMapData()
		await dispatch(api.endpoints.addMap.initiate({ name: 'Default', data, thumbnail: await mapThumbnail(data) }))
		await mapsNow(dispatch)
	})().finally(() => {
		ensuring = null
	})
	return ensuring
}

const shapeOf = (mapId, surfaceId, surface, d, fill) => {
	const { data, x, y } = resetPathOrigin(d)
	const obj = {
		type: 'path',
		name: surface.name,
		data,
		fill,
		locked: true,
		map: { id: mapId, surface: surfaceId, role: surface.role },
	}
	return { ...obj, ...positionFromOrigin(obj, { x, y }, {}) }
}

const newIds = (before, getState) => Object.keys(getState().live.objects).filter(id => !before.has(id))

/** Adds `shapes` as one shape, or one group of them named `name`; returns the root-level id. */
const addShapes = (dispatch, getState, shapes, name) => {
	let before = new Set(Object.keys(getState().live.objects))
	dispatch(addObjects(shapes))
	const ids = newIds(before, getState)
	if (ids.length < 2) return ids[0]
	before = new Set(Object.keys(getState().live.objects))
	dispatch(groupObjects(ids))
	const [group] = newIds(before, getState)
	dispatch(updateObject({ id: group, name, locked: true }))
	return group
}

/** The `[id, surface]` entries of `map` no shape in `objects` links to. */
export const unplacedSurfaces = (objects, map) => {
	const placed = new Set()
	for (const obj of Object.values(objects ?? {})) if (obj.map?.id === map?.id) placed.add(obj.map.surface)
	return Object.entries(map?.data?.surfaces ?? {}).filter(([id]) => !placed.has(id))
}

/** Map's surfaces (`only` those ids, if given) with no shape in the project go onto the stage, each back through its slice: lit ones transparent at the back, cuts black on top, geometry pinned. Inside the caller's batch. */
const placeSurfaces = (map, only) => (dispatch, getState) => {
	const { data } = map
	const unplaced = unplacedSurfaces(getState().live.objects, map).filter(([id]) => !only || only.includes(id))
	const shapes = role =>
		unplaced
			.filter(([, s]) => s.role === role)
			.map(([id, s]) => {
				const d = mapPath(s.path, outputToStage(data, s))
				return d && shapeOf(map.id, id, s, d, role === 'cut' ? CUT_FILL : NO_FILL)
			})
			.filter(Boolean)
	const lit = shapes('lit')
	const cut = shapes('cut')
	if (!lit.length && !cut.length) return false

	const keys = []
	if (lit.length) {
		const id = addShapes(dispatch, getState, lit, map.name)
		dispatch(sendObjectToBack([id]))
		keys.push(...linkPinKeys(getState().live.objects, id))
	}
	if (cut.length) {
		const id = addShapes(dispatch, getState, cut, `${map.name} cuts`)
		const objects = getState().live.objects
		const subtree = objectPinKeys(objects, id, 'existence').map(k => k.slice('objects.'.length))
		keys.push(...linkPinKeys(objects, id))
		for (const oid of subtree) if (objects[oid]?.type !== 'group') keys.push(`objects.${oid}.fill`)
	}
	return dispatch(pinKeys(keys))
}

/** A carried object takes only its pinned props from live, so the link, its lock and its name travel with the geometry. */
const LINK_PROPS = new Set(['map', 'locked', 'name'])

/** Geometry and link pin keys of `id`'s subtree. */
const linkPinKeys = (objects, id) => [
	...objectPinKeys(objects, id, 'geometry'),
	...objectPinKeys(objects, id, 'all').filter(k => LINK_PROPS.has(k.slice(k.lastIndexOf('.') + 1))),
]

const countNames = names => names.reduce((m, n) => m.set(n, (m.get(n) ?? 0) + 1), new Map())

/** The link patch for every shape linked to a map other than `map`: a shape whose surface name occurs once in its own map (the shapes' names, for a map out of reach) and once in `map` takes that surface, unless a shape has it already; any other detaches. */
const relinkPatches = (objects, maps, map) => {
	const target = Object.entries(map.data.surfaces ?? {})
	const targetNames = countNames(target.map(([, s]) => s.name))
	const claimed = new Set()
	const byMap = new Map()
	for (const [id, o] of Object.entries(objects)) {
		if (o.map?.id === map.id) claimed.add(o.map.surface)
		else if (o.map?.id) byMap.set(o.map.id, [...(byMap.get(o.map.id) ?? []), [id, o]])
	}
	const patches = []
	for (const [mapId, links] of byMap) {
		const data = maps.find(m => m.id === mapId)?.data
		const nameOf = o => (data ? data.surfaces?.[o.map.surface]?.name : o.name)
		const own = countNames(
			data ? Object.values(data.surfaces ?? {}).map(s => s.name) : links.map(([, o]) => o.name)
		)
		for (const [id, o] of links) {
			const name = nameOf(o)
			const hit =
				name != null &&
				own.get(name) === 1 &&
				targetNames.get(name) === 1 &&
				target.find(([, s]) => s.name === name)
			if (hit && !claimed.has(hit[0])) {
				claimed.add(hit[0])
				patches.push({ id, map: { id: map.id, surface: hit[0], role: hit[1].role } })
			} else patches.push({ id, map: '' })
		}
	}
	return patches
}

const hasOtherLinks = (objects, id) => Object.values(objects).some(o => o.map?.id && o.map.id !== id)

/** Map `id` becomes active; `relink` moves other maps' shapes onto it (`relinkPatches`) and puts its surfaces with no shape onto the stage, one undo step. */
const swapNow =
	({ id, relink }) =>
	(dispatch, getState) => {
		const s = getState()
		const map = selectMaps(s).find(m => m.id === id)
		if (!map?.data) return false
		dispatch(activateNow(id))
		if (!relink) return true
		dispatch(startBatch())
		try {
			// `mapLinked`: the derive puts each relinked shape on its new surface.
			dispatch({
				...updateObjects(relinkPatches(s.live.objects, selectMaps(s), map)),
				meta: { mapLinked: true },
			})
			return dispatch(placeSurfaces(map))
		} finally {
			dispatch(endBatch())
		}
	}
registerAction('swapMap', params => swapNow(params))

/** Map `id` becomes active; with another map's shapes on the stage, asks first whether they relink to it (its surfaces left with no shape added) or keep their map. */
export const activateMap = id => (dispatch, getState) => {
	const s = getState()
	const map = selectMaps(s).find(m => m.id === id)
	if (!map?.data) return false
	// Already shown here: nothing to swap, but the project may not remember it yet.
	if (id === selectActiveMapId(s) || !hasOtherLinks(s.live.objects, id)) return dispatch(swapNow({ id }))
	dispatch(
		requestConfirm({
			title: 'Another Map on the Stage',
			message: `Shapes from another map are on the stage. Relink them to "${map.name}" by surface name, or keep them on their map?`,
			action: { type: 'swapMap', params: { id, relink: true } },
			noAction: { type: 'swapMap', params: { id } },
			yesLabel: 'Relink',
			noLabel: 'Keep',
		})
	)
	return true
}

/** Of the edited map's surfaces `ids`: `add`, saved ones with no shape in the project, and `unsaved`, ones only the draft has. */
export const surfacesToPlace = ids => (_, getState) => {
	const s = getState()
	const map = selectMaps(s).find(m => m.id === s.maps.editing?.id)
	const unplaced = new Set(unplacedSurfaces(s.live.objects, map).map(([id]) => id))
	return {
		add: ids.filter(id => unplaced.has(id)),
		unsaved: ids.filter(id => !map?.data?.surfaces?.[id]),
	}
}

/** The edited map's saved surfaces `ids` with no shape in the project go onto the stage (`placeSurfaces`), one undo step. */
export const addSurfacesToProject = ids => (dispatch, getState) => {
	const s = getState()
	const map = selectMaps(s).find(m => m.id === s.maps.editing?.id)
	if (!map?.data) return false
	dispatch(startBatch())
	try {
		return dispatch(placeSurfaces(map, ids))
	} finally {
		dispatch(endBatch())
	}
}

/** `[[id, { id: mapId, surface }]]` for every map-linked shape under `ids` (groups walked). */
export const mapLinks = (objects, ids) => {
	const out = []
	const walk = (id, depth) => {
		const obj = objects?.[id]
		if (!obj || depth > 16) return
		if (obj.map?.id) out.push([id, obj.map])
		for (const cid of obj.children ?? []) walk(cid, depth + 1)
	}
	for (const id of ids ?? []) walk(id, 0)
	return out
}

/** Map-linked shapes under `ids` lose their link; they keep their geometry and pins, a pinned `map` following live to ''. */
export const detachFromMap = ids => (dispatch, getState) => {
	const links = mapLinks(getState().live.objects, ids)
	if (links.length) dispatch(updateObjects(links.map(([id]) => ({ id, map: '' }))))
	return links.length
}
